// ═══════════════════════════════════════════════════════
// LOGIN PAGE — Autenticação do portal
// ═══════════════════════════════════════════════════════
import { useState } from "react";
import { signIn, supabase, logAudit } from "../supabaseClient.js";
import { RADIUS, TYPE, SP, LIGHT } from "../shared/theme.js";

interface LoginPageProps {
  sessionExpired?: boolean;
  onDismiss?: () => void;
}

export default function LoginPage({ sessionExpired = false, onDismiss }: LoginPageProps) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [load, setLoad] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [resetOk, setResetOk] = useState(false);

  // ── Login com Supabase Auth ──────────────────────────
  const go = async () => {
    if (!email || !pass) {
      setErr("Preencha e-mail e senha.");
      return;
    }
    setLoad(true);
    setErr("");
    const { error } = await signIn(email.trim().toLowerCase(), pass);
    if (error) {
      setErr(
        error.message === "Invalid login credentials"
          ? "E-mail ou senha incorretos. Verifique suas credenciais."
          : error.message
      );
      await logAudit("login_failed", { details: { email: email.trim().toLowerCase() } });
    }
    setLoad(false);
    // Em caso de sucesso, o onAuthStateChange no App root detecta a sessão automaticamente
  };

  // ── Reset de senha via e-mail (Supabase) ─────────────
  const doReset = async () => {
    if (!email) {
      setErr("Informe seu e-mail para recuperação.");
      return;
    }
    setLoad(true);
    setErr("");
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: window.location.origin,
    });
    setLoad(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setResetOk(true);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        background: `linear-gradient(135deg,${LIGHT.navy} 0%,${LIGHT.navy2} 60%,#0F3460 100%)`,
        alignItems: "center",
        justifyContent: "center",
        fontFamily: TYPE.fontSans,
      }}
    >
      <div
        style={{
          position: "fixed",
          top: -100,
          right: -100,
          width: 400,
          height: 400,
          borderRadius: RADIUS.full,
          background: `rgba(201,162,39,0.08)`,
          pointerEvents: "none",
        }}
      />
      <div style={{ width: "100%", maxWidth: 420, padding: SP[6] }}>
        <div style={{ textAlign: "center", marginBottom: SP[9] }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: SP[3],
              marginBottom: SP[2],
            }}
          >
            <div
              style={{
                width: 50,
                height: 50,
                borderRadius: RADIUS.md,
                background: LIGHT.gold,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: TYPE["2xl"],
                fontWeight: TYPE.heavy,
                color: LIGHT.navy,
              }}
            >
              L
            </div>
            <div>
              <div style={{ fontSize: TYPE["2xl"], fontWeight: TYPE.heavy, color: "#fff" }}>
                LPA <span style={{ color: LIGHT.gold }}>Wealth</span>
              </div>
              <div
                style={{
                  fontSize: TYPE.xs,
                  color: "rgba(255,255,255,0.4)",
                  letterSpacing: 2.5,
                  textTransform: "uppercase",
                }}
              >
                Investment Advisory
              </div>
            </div>
          </div>
          <p style={{ color: "rgba(255,255,255,0.4)", fontSize: TYPE.sm, margin: 0 }}>
            Portal Exclusivo do Cliente
          </p>
        </div>

        {/* Banner: sessão expirada */}
        {sessionExpired && (
          <div
            style={{
              background: "rgba(220,38,38,0.15)",
              border: "1px solid rgba(220,38,38,0.4)",
              borderRadius: RADIUS.md,
              padding: `${SP[3]} ${SP[4]}`,
              marginBottom: SP[4],
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: SP[2],
            }}
          >
            <span style={{ color: "#FCA5A5", fontSize: TYPE.base, fontWeight: TYPE.semibold }}>
              ⏱️ Sua sessão expirou. Faça login novamente.
            </span>
            <button
              onClick={onDismiss}
              style={{
                background: "none",
                border: "none",
                color: "rgba(255,255,255,0.4)",
                cursor: "pointer",
                fontSize: TYPE.lg,
                padding: 0,
                lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        <div
          style={{
            background: "#fff",
            borderRadius: RADIUS.xl,
            padding: SP[9],
            boxShadow: "0 24px 60px rgba(0,0,0,0.35)",
          }}
        >
          {resetOk ? (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: TYPE["3xl"], marginBottom: SP[3] }}>📧</div>
              <h2
                style={{
                  fontSize: TYPE.xl,
                  fontWeight: TYPE.bold,
                  color: LIGHT.navy,
                  margin: "0 0 8px",
                }}
              >
                E-mail enviado!
              </h2>
              <p style={{ color: LIGHT.muted, fontSize: TYPE.base }}>
                Verifique sua caixa de entrada e clique no link para redefinir sua senha.
              </p>
              <button
                onClick={() => {
                  setForgot(false);
                  setResetOk(false);
                }}
                style={{
                  marginTop: SP[5],
                  padding: `${SP[2]} ${SP[6]}`,
                  borderRadius: RADIUS.sm,
                  border: "none",
                  background: LIGHT.navy,
                  color: "#fff",
                  fontWeight: TYPE.bold,
                  cursor: "pointer",
                }}
              >
                Voltar ao login
              </button>
            </div>
          ) : forgot ? (
            <>
              <h2
                style={{
                  fontSize: TYPE.xl,
                  fontWeight: TYPE.bold,
                  color: LIGHT.navy,
                  margin: "0 0 5px",
                }}
              >
                Recuperar senha
              </h2>
              <p style={{ color: LIGHT.muted, fontSize: TYPE.base, margin: "0 0 20px" }}>
                Informe seu e-mail cadastrado
              </p>
              {err && (
                <div
                  style={{
                    background: LIGHT.dangerBg,
                    color: LIGHT.danger,
                    padding: `${SP[2]} ${SP[3]}`,
                    borderRadius: RADIUS.sm,
                    fontSize: TYPE.sm,
                    marginBottom: SP[3],
                  }}
                >
                  {err}
                </div>
              )}
              <label
                style={{
                  fontSize: TYPE.sm,
                  fontWeight: TYPE.semibold,
                  color: LIGHT.sub,
                  display: "block",
                  marginBottom: SP[1],
                }}
              >
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com.br"
                style={{
                  width: "100%",
                  padding: `${SP[2]} ${SP[3]}`,
                  borderRadius: RADIUS.md,
                  border: `1.5px solid ${LIGHT.border}`,
                  fontSize: TYPE.md,
                  outline: "none",
                  boxSizing: "border-box",
                  marginBottom: SP[5],
                }}
              />
              <button
                onClick={doReset}
                style={{
                  width: "100%",
                  padding: `${SP[3]} 0`,
                  borderRadius: RADIUS.md,
                  border: "none",
                  cursor: "pointer",
                  background: `linear-gradient(135deg,${LIGHT.navy} 0%,${LIGHT.navy2} 100%)`,
                  color: "#fff",
                  fontSize: TYPE.lg,
                  fontWeight: TYPE.bold,
                }}
              >
                {load ? "Enviando..." : "Enviar link de redefinição"}
              </button>
              <button
                onClick={() => {
                  setForgot(false);
                  setErr("");
                }}
                style={{
                  display: "block",
                  margin: `${SP[3]} auto 0`,
                  background: "none",
                  border: "none",
                  color: LIGHT.muted,
                  fontSize: TYPE.sm,
                  cursor: "pointer",
                }}
              >
                ← Voltar
              </button>
            </>
          ) : (
            <>
              <h2
                style={{
                  fontSize: TYPE.xl,
                  fontWeight: TYPE.bold,
                  color: LIGHT.navy,
                  margin: "0 0 5px",
                }}
              >
                Bem-vindo
              </h2>
              <p style={{ color: LIGHT.muted, fontSize: TYPE.base, margin: `0 0 ${SP[6]}` }}>
                Acesse sua carteira com segurança
              </p>
              {err && (
                <div
                  style={{
                    background: LIGHT.dangerBg,
                    color: LIGHT.danger,
                    padding: `${SP[2]} ${SP[3]}`,
                    borderRadius: RADIUS.sm,
                    fontSize: TYPE.sm,
                    marginBottom: SP[3],
                  }}
                >
                  {err}
                </div>
              )}
              <label
                style={{
                  fontSize: TYPE.sm,
                  fontWeight: TYPE.semibold,
                  color: LIGHT.sub,
                  display: "block",
                  marginBottom: SP[1],
                }}
              >
                E-mail
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com.br"
                style={{
                  width: "100%",
                  padding: `${SP[2]} ${SP[3]}`,
                  borderRadius: RADIUS.md,
                  border: `1.5px solid ${LIGHT.border}`,
                  fontSize: TYPE.md,
                  outline: "none",
                  boxSizing: "border-box",
                  marginBottom: SP[3],
                }}
              />
              <label
                style={{
                  fontSize: TYPE.sm,
                  fontWeight: TYPE.semibold,
                  color: LIGHT.sub,
                  display: "block",
                  marginBottom: SP[1],
                }}
              >
                Senha
              </label>
              <input
                type="password"
                value={pass}
                onChange={(e) => setPass(e.target.value)}
                placeholder="••••••••"
                onKeyDown={(e) => e.key === "Enter" && go()}
                style={{
                  width: "100%",
                  padding: `${SP[2]} ${SP[3]}`,
                  borderRadius: RADIUS.md,
                  border: `1.5px solid ${LIGHT.border}`,
                  fontSize: TYPE.md,
                  outline: "none",
                  boxSizing: "border-box",
                  marginBottom: SP[6],
                }}
              />
              <button
                onClick={go}
                disabled={load}
                style={{
                  width: "100%",
                  padding: `${SP[3]} 0`,
                  borderRadius: RADIUS.md,
                  border: "none",
                  cursor: "pointer",
                  background: `linear-gradient(135deg,${LIGHT.navy} 0%,${LIGHT.navy2} 100%)`,
                  color: "#fff",
                  fontSize: TYPE.lg,
                  fontWeight: TYPE.bold,
                  opacity: load ? 0.7 : 1,
                }}
              >
                {load ? "Autenticando..." : "Entrar"}
              </button>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: SP[3] }}>
                <button
                  onClick={() => {
                    setForgot(true);
                    setErr("");
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: TYPE.sm,
                    color: LIGHT.muted,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  Esqueci minha senha
                </button>
                <a
                  href="mailto:rafael@lpawealth.com.br"
                  style={{
                    fontSize: TYPE.sm,
                    color: LIGHT.navy2,
                    textDecoration: "none",
                    fontWeight: TYPE.semibold,
                  }}
                >
                  Suporte LPA
                </a>
              </div>
            </>
          )}
        </div>

        <p
          style={{
            textAlign: "center",
            color: "rgba(255,255,255,0.2)",
            fontSize: TYPE.xs,
            marginTop: SP[5],
          }}
        >
          🔒 TLS 1.3 · Supabase Auth · LPA Wealth © 2026
        </p>
      </div>
    </div>
  );
}
